import { SERVER_QUERY } from '@/router/query-params'
import { decodeUACCookieValue, getSDKUACFromCookies, updateSDKUACCookie } from '@/utils/cookie-parser'

export type UACParams = {
    source?: string
    medium?: string
    campaign?: string
    campaign_id?: string
    term?: string
    content?: string
}

export type ResolvedUACParams = {
    params: UACParams
    updatedParams: UACParams | null
}

function extractQueryStringValue(searchParams: URLSearchParams, key: string): string | undefined {
    const values = searchParams.getAll(key)
    if (values.length === 0) {
        return undefined
    }
    return values[values.length - 1]
}

const allEmptyFields = (obj: Record<string, unknown>) => Object.values(obj).every((value) => value === undefined)
const hasNonEmptyFields = (obj: Record<string, unknown>) => Object.values(obj).some((value) => !!value)

export function generateUACCookieValue({
    searchParams,
    referer,
    currentHost,
    existingUACCookie,
}: {
    searchParams: URLSearchParams
    referer: string | undefined
    currentHost: string
    existingUACCookie: string | undefined
}): UACParams | null {
    const userAcquisitionParams: Record<string, string | undefined> = {
        source: extractQueryStringValue(searchParams, SERVER_QUERY.UTM_SOURCE),
        medium: extractQueryStringValue(searchParams, SERVER_QUERY.UTM_MEDIUM),
        campaign: extractQueryStringValue(searchParams, SERVER_QUERY.UTM_CAMPAIGN),
        campaign_id: extractQueryStringValue(searchParams, SERVER_QUERY.UTM_CAMPAIGN_ID),
        term: extractQueryStringValue(searchParams, SERVER_QUERY.UTM_TERM),
        content: extractQueryStringValue(searchParams, SERVER_QUERY.UTM_CONTENT),
    }

    if (allEmptyFields(userAcquisitionParams) && existingUACCookie === undefined) {
        if (referer) {
            if (!referer.replace(/^https?:\/\//, '').startsWith(currentHost)) {
                userAcquisitionParams.source = referer
                userAcquisitionParams.medium = 'referral'
            }
        } else {
            userAcquisitionParams.source = 'direct'
            userAcquisitionParams.medium = 'none'
        }
    }

    if (hasNonEmptyFields(userAcquisitionParams)) {
        return userAcquisitionParams
    }

    return null
}

export function resolveUACParams({
    searchParams,
    referer,
    currentHost,
    existingUACCookie,
}: {
    searchParams: URLSearchParams
    referer: string | undefined
    currentHost: string
    existingUACCookie: string | undefined
}): ResolvedUACParams {
    const updatedParams = generateUACCookieValue({ searchParams, referer, currentHost, existingUACCookie })

    return {
        params: updatedParams || (decodeUACCookieValue(existingUACCookie) as UACParams | null) || {},
        updatedParams,
    }
}

function getUtmCid(utmContent: string | undefined): string {
    return utmContent?.match(/cid-([^|]+)/)?.[1] || '-'
}

export function getUACCampaignId(params: UACParams): string | undefined {
    return params.campaign_id || params.content?.match(/cid-([^|]+)/)?.[1]
}

function getUtmAssetGroup(utmContent: string | undefined): string {
    return utmContent?.match(/gid-([^|]+)/)?.[1] || '-'
}

export function transformUACtoAdTargetings(params: Record<string, string>): Record<string, string> {
    const res: Record<string, string> = {}
    const utmContent = params.content
    for (const key of ['source', 'medium', 'campaign', 'campaign_id', 'assetgroup_id']) {
        if (params[key]) {
            res[`utm_${key}`] = params[key]
        }
    }
    if (res['utm_campaign_id'] === undefined) {
        res['utm_campaign_id'] = getUtmCid(utmContent)
    }
    if (res['utm_assetgroup_id'] === undefined) {
        res['utm_assetgroup_id'] = getUtmAssetGroup(utmContent)
    }
    return res
}

export function initSDKUACCookie(): UACParams | null {
    const searchParams = new URLSearchParams(window.location.search)
    const referer = document.referrer || undefined
    const currentHost = window.location.hostname
    const existingCookie = getSDKUACFromCookies(document.cookie)

    const uacValue = generateUACCookieValue({
        searchParams,
        referer,
        currentHost,
        existingUACCookie: existingCookie ? 'existing' : undefined,
    })

    if (uacValue) {
        updateSDKUACCookie(uacValue)
        return uacValue
    }

    return existingCookie as UACParams | null
}
